import { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import { LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import type { PanInfo } from 'framer-motion';
import { hapticFeedback } from '@telegram-apps/sdk-react';
import { Hand } from 'lucide-react';
import type { ModuleMeta } from '../../harness/ModuleRegistry';
import { AtmosphericLayer } from '../_shared/AtmosphericLayer';
import { Sigil } from '../_nav/Sigil';
import { Wordmark } from '../_nav/Wordmark';

// 04c Kanban Board — Wave 3 drag-and-drop + final-reveal mask.
//
// Layers:
//   • Mount stagger 60ms per card (scale 0.95 → 1 + fade) — DESIGN.md §7.2
//   • Drag via `motion.article` + Framer Motion drag — GDD §5.1
//   • Drop-zone hover state — dashed cyan border on `<СДЕЛАНО>` column (§9.4)
//   • Drop snap — `layoutId` magic-move from in-work `<ul>` to done `<ul>`
//     with spring 400ms + a one-shot cyan-glow flash (§6.1 shadow-snap-glow)
//     + `hapticFeedback.impactOccurred('light')` (defensive try/catch).
//   • Final reveal — when all 5 cards reach «СДЕЛАНО», the «5-7 дней» payoff
//     swaps from muted to `--text-display-mega` Lora with sweep clip-path
//     mask 1000ms ease-in-out + `--shadow-final-glow` halo (§7.1).
//   • Reset link — Helvetica 12px text-muted under the final reveal returns
//     the board to its initial state.
//
// Reduced motion: drag is still allowed; mount stagger / mask / spring
// collapse to instant. CSS-side `@media (prefers-reduced-motion: reduce)`
// kills the keyframes; JS-side `useReducedMotion()` flattens the spring
// transition object.

export const meta: ModuleMeta = {
  id: '04c-game-kanban',
  title: 'Kanban Board · drag-drop',
  status: 'approved',
  viewport: '390x844',
};

interface CardData {
  id: string;
  text: string;
}

const IN_WORK_CARDS: CardData[] = [
  { id: 'post-pre-match', text: 'Пост перед матчем' },
  { id: 'preview-matchday', text: 'Превью матчдея' },
  { id: 'keeper-interview', text: 'Интервью с вратарём' },
  { id: 'win-stories', text: 'Сторис после победы' },
  { id: 'derby-gamification', text: 'Геймификация к дерби' },
];

const DONE_CARDS_BASELINE: CardData[] = [
  { id: 'broadcast', text: 'Рассылка по базе' },
];

const DROP_FLASH_MS = 600;

function DoneCheckmark() {
  return (
    <svg
      role="presentation"
      aria-hidden="true"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      className="module-04c-checkmark"
    >
      <path
        d="M3.5 8.5 L6.5 11.5 L12.5 5"
        stroke="#1F1F1F"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function tryHaptic(style: 'light' | 'medium' | 'soft' = 'light'): void {
  try {
    hapticFeedback.impactOccurred(style);
  } catch {
    // browser / SDK unavailable — silent per lib/telegram.ts defensive pattern.
  }
}

export default function KanbanModule() {
  const [doneIds, setDoneIds] = useState<Set<string>>(() => new Set());
  const [hoveredDone, setHoveredDone] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [justDroppedId, setJustDroppedId] = useState<string | null>(null);
  const doneColumnRef = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();

  const allDone = doneIds.size === IN_WORK_CARDS.length;

  const isOverDoneZone = useCallback((point: { x: number; y: number }): boolean => {
    const column = doneColumnRef.current;
    if (!column) return false;
    const rect = column.getBoundingClientRect();
    return (
      point.x >= rect.left &&
      point.x <= rect.right &&
      point.y >= rect.top &&
      point.y <= rect.bottom
    );
  }, []);

  const handleDragStart = useCallback((cardId: string) => {
    setDraggingId(cardId);
    tryHaptic('light');
  }, []);

  const handleDrag = useCallback(
    (_e: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) => {
      setHoveredDone(isOverDoneZone(info.point));
    },
    [isOverDoneZone],
  );

  const handleDragEnd = useCallback(
    (cardId: string, _e: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) => {
      const droppedInDone = isOverDoneZone(info.point);
      setHoveredDone(false);
      setDraggingId(null);
      if (!droppedInDone) return;
      setDoneIds((prev) => {
        const next = new Set(prev);
        next.add(cardId);
        return next;
      });
      setJustDroppedId(cardId);
      tryHaptic('light');
      window.setTimeout(() => {
        setJustDroppedId((current) => (current === cardId ? null : current));
      }, DROP_FLASH_MS);
    },
    [isOverDoneZone],
  );

  const handleReset = useCallback(() => {
    setDoneIds(new Set());
    setJustDroppedId(null);
  }, []);

  // Tap-to-done — hint says «или тапни по карточке», so honour that.
  // Same end state as a drag-and-drop. Distinguishes tap vs drag by
  // checking we're not currently dragging this card.
  const handleCardTap = useCallback((cardId: string) => {
    if (draggingId === cardId) return; // tap fired at drag-end; ignore
    setDoneIds((prev) => {
      const next = new Set(prev);
      next.add(cardId);
      return next;
    });
    setJustDroppedId(cardId);
    tryHaptic('light');
    window.setTimeout(() => {
      setJustDroppedId((current) => (current === cardId ? null : current));
    }, DROP_FLASH_MS);
  }, [draggingId]);

  const dropTransition = prefersReducedMotion
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 400, damping: 30 };

  const inWorkVisible = IN_WORK_CARDS.filter((card) => !doneIds.has(card.id));
  const movedToDone = IN_WORK_CARDS.filter((card) => doneIds.has(card.id));
  const doneCards = [...DONE_CARDS_BASELINE, ...movedToDone];

  return (
    <section
      className="relative min-h-full w-full bg-surface-main text-text-primary"
      style={{
        paddingTop: 'max(var(--safe-top, 0px), 0.75rem)',
        paddingBottom: 'max(var(--safe-bottom, 0px), 1.5rem)',
      }}
    >
      <AtmosphericLayer spheres={1} shafts={0} intensity="reduced" />

      <Wordmark variant="nav" />

      <div className="pointer-events-none absolute bottom-sp-7 right-sp-5 z-[1]" aria-hidden>
        <Sigil variant="watermark" />
      </div>

      <div
        className="relative z-10 flex h-full flex-col px-sp-6"
        style={{ paddingTop: 'calc(max(var(--safe-top, 0px), 1.25rem) + 56px)' }}
      >
        <header className="flex flex-col gap-sp-5">
          <h2 className="font-display text-display leading-[1.1] tracking-[-0.02em] text-text-primary">
            Инструменты для
            <br />
            медиа-команд
          </h2>

          <p className="flex items-start gap-sp-2 font-ui text-body-sm leading-[1.5] text-text-muted">
            <Hand className="mt-[2px] h-[16px] w-[16px] shrink-0" strokeWidth={1.5} />
            <span>Тяни задачу в «Сделано» или тапни по карточке</span>
          </p>
        </header>

        <LayoutGroup>
          <div className="module-04c-board mt-sp-7 grid grid-cols-2 gap-sp-3">
            <section className="module-04c-column">
              <h3 className="module-04c-column-header">В работе</h3>
              <ul className="flex flex-col gap-sp-2">
                {inWorkVisible.map((card) => {
                  const staggerIdx = IN_WORK_CARDS.findIndex((c) => c.id === card.id) + 1;
                  return (
                    <li
                      key={card.id}
                      className={clsx(
                        'module-04c-card-mount',
                        `module-04c-card--stagger-${staggerIdx}`,
                      )}
                    >
                      <motion.article
                        layoutId={`kanban-card-${card.id}`}
                        layout="position"
                        drag
                        dragSnapToOrigin
                        dragMomentum={false}
                        dragElastic={0.15}
                        whileTap={{ scale: 0.98 }}
                        whileDrag={{ scale: 1.04, zIndex: 50 }}
                        onDragStart={() => handleDragStart(card.id)}
                        onDrag={handleDrag}
                        onDragEnd={(e, info) => handleDragEnd(card.id, e, info)}
                        onTap={() => handleCardTap(card.id)}
                        transition={dropTransition}
                        className={clsx(
                          'module-04c-card module-04c-card--idle',
                          draggingId === card.id && 'module-04c-card--dragging',
                        )}
                        style={{ touchAction: 'pan-y' }}
                      >
                        <span>{card.text}</span>
                      </motion.article>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section
              ref={doneColumnRef}
              className={clsx(
                'module-04c-column',
                hoveredDone && 'module-04c-column--drop-active',
              )}
            >
              <h3 className="module-04c-column-header">Сделано</h3>
              <ul className="flex flex-col gap-sp-2">
                {doneCards.map((card) => (
                  <li key={card.id}>
                    <motion.article
                      layoutId={`kanban-card-${card.id}`}
                      layout="position"
                      transition={dropTransition}
                      className={clsx(
                        'module-04c-card module-04c-card--done',
                        justDroppedId === card.id && 'module-04c-card--just-dropped',
                      )}
                    >
                      <span>{card.text}</span>
                      <DoneCheckmark />
                    </motion.article>
                  </li>
                ))}
                {!allDone && (
                  <li
                    aria-hidden
                    className={clsx(
                      'module-04c-dropzone',
                      hoveredDone && 'module-04c-dropzone--active',
                    )}
                  />
                )}
              </ul>
            </section>
          </div>
        </LayoutGroup>

        <div className="module-04c-final mt-sp-10">
          <p className="font-ui text-rubric uppercase tracking-[0.2em] text-text-muted">
            Производственный спринт
            <br />
            любого проекта
          </p>
          <p
            className={clsx(
              'module-04c-final-payoff mt-sp-4 font-display',
              allDone && 'module-04c-final-payoff--revealed',
            )}
          >
            5-7 дней
          </p>
          <p className="mt-sp-5 font-ui text-body leading-[1.5] text-text-muted">
            С инструментами ИИ и экспертизой команды RAFL
          </p>
          {allDone && (
            <button
              type="button"
              onClick={handleReset}
              className="module-04c-reset mt-sp-4 font-ui"
            >
              ещё раз
            </button>
          )}
        </div>

        <div
          className="hint-arrow-pulse mt-sp-7 flex items-center justify-center gap-sp-3 pb-sp-7 text-text-muted"
          aria-hidden
        >
          <span className="inline-block h-[14px] w-[14px] rounded-full border border-current" />
          <span className="text-[1.25rem] leading-none">↓</span>
        </div>
      </div>
    </section>
  );
}
